type Primitive = string | number | boolean | null | undefined;

type DotPrefix<T extends string> = T extends "" ? "" : `_${T}`;

type FlattenKeys<T> = T extends Primitive | Date | Array<any>
	? ""
	: T extends object
		? {
				[K in keyof T]-?: `${K & string}${DotPrefix<FlattenKeys<NonNullable<T[K]>>>}`;
			}[keyof T]
		: "";

export type FlattenObject<T> = {
	[K in FlattenKeys<T>]: K extends keyof T
		? T[K]
		: K extends `${infer F}_${infer R}`
			? F extends keyof T
				? NonNullable<T[F]> extends object
					? R extends FlattenKeys<NonNullable<T[F]>>
						? FlattenObject<NonNullable<T[F]>>[R]
						: never
					: never
				: never
			: never;
};

export function flattenObject<T extends object>(obj: T): FlattenObject<T> {
	const result: Record<string, any> = {};

	function flatten(obj: any, prefix = "") {
		for (const key in obj) {
			const value = obj[key];
			const newKey = prefix ? `${prefix}_${key}` : key;

			if (value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date)) {
				flatten(value, newKey);
			}
			else {
				result[newKey] = value;
			}
		}
	}

	flatten(obj);
	return result as FlattenObject<T>;
}
